import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
const SortableItem = React.memo(({ id, content, onDelete, onEdit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className="flex items-center gap-4 p-4 mb-2 bg-white border rounded shadow-sm group"
    >
      <div 
        {...attributes} 
        {...listeners}
        className="cursor-grab p-2 hover:bg-gray-100 rounded"
      >
        ≡
      </div>
      <div className="flex-1">
        <input
          type="number"
          value={content.quantity}
          onChange={(e) => onEdit(id, { ...content, quantity: parseInt(e.target.value) || 0 })}
          className="w-20 p-2 mr-2 border rounded"
          min="0"
        />
        <input
          type="text"
          value={content.description}
          onChange={(e) => onEdit(id, { ...content, description: e.target.value })}
          className="flex-1 p-2 border rounded"
          placeholder="Item description"
        />
      </div>
      <button
        type="button"
        onClick={() => onDelete(id)}
        className="opacity-0 group-hover:opacity-100 p-2 text-red-600 hover:bg-red-50 rounded transition-opacity"
      >
        ×
      </button>
    </div>
  );
});

// Main Component
export const ProductBoxEditor = ({ initialData = null, onChange }) => {
  const [items, setItems] = useState([]);
  const isInitialMount = useRef(true);
  const prevInitialDataRef = useRef(null);
  const isInternalUpdate = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Only initialize items from initialData once when component mounts or when initialData changes from external source
  useEffect(() => {
    // Skip if this is an internal update
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    // Deep compare initialData with previous initialData
    const initialDataStr = JSON.stringify(initialData);
    const prevInitialDataStr = JSON.stringify(prevInitialDataRef.current);
    
    if (initialDataStr !== prevInitialDataStr) {
      prevInitialDataRef.current = initialData;
      
      if (initialData && Array.isArray(initialData)) {
        if (initialData.length > 0) {
          try {
            const parsedData = typeof initialData === 'string' 
              ? JSON.parse(initialData) 
              : initialData;
              
            // Use a stable ID that doesn't change on every render
            setItems(parsedData.map((item, index) => ({
              id: `item-${index}`,
              content: item,
              order: index + 1
            })));
          } catch (error) {
            console.error('Error parsing initial data:', error);
          }
        } else if (initialData.length === 0 && items.length > 0) {
          // Clear items if initialData is empty array
          setItems([]);
        }
      }
    }
  }, [initialData, items.length]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setItems((currentItems) => {
        const oldIndex = currentItems.findIndex((item) => item.id === active.id);
        const newIndex = currentItems.findIndex((item) => item.id === over.id);
        
        const newItems = arrayMove(currentItems, oldIndex, newIndex)
          .map((item, index) => ({
            ...item,
            order: index + 1
          }));
        
        return newItems;
      });
    }
  }, []);

  const addItem = useCallback(() => {
    setItems((currentItems) => {
      const newItem = {
        id: `item-new-${Date.now()}`,
        content: { quantity: 1, description: '' },
        order: currentItems.length + 1
      };
      return [...currentItems, newItem];
    });
  }, []);

  const deleteItem = useCallback((id) => {
    setItems((currentItems) => {
      const newItems = currentItems
        .filter(item => item.id !== id)
        .map((item, index) => ({
          ...item,
          order: index + 1
        }));
      return newItems;
    });
  }, []);

  const editItem = useCallback((id, newContent) => {
    setItems((currentItems) => 
      currentItems.map(item => 
        item.id === id 
          ? { ...item, content: newContent }
          : item
      )
    );
  }, []);

  // Update parent component whenever items change
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Set flag to prevent the initialData effect from running
    isInternalUpdate.current = true;
    
    // Format output data
    const output = items
      .sort((a, b) => a.order - b.order)
      .map(item => item.content);
    
    onChange(output);
  }, [items, onChange]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={addItem}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Add Item
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map(item => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {items.length > 0 ? (
              items.map((item) => (
                <SortableItem
                  key={item.id}
                  id={item.id}
                  content={item.content}
                  onDelete={deleteItem}
                  onEdit={editItem}
                />
              ))
            ) : (
              <div className="py-8 text-center text-gray-400 border-2 border-dashed rounded">
                No items added yet. Click "Add Item" to begin.
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};
